import * as React from "react";

import {Button, Form, Input, Popconfirm, Select} from "antd";
import {FileType, IFile} from "../../store/files/types";
import {IAddFileAsyncRequestPayload} from "../../store/files/actions";
import {SuperAdminZone} from "../AccessControl";

const {Option} = Select;


interface IAddFileFormProps {
    file: IFile;
}

export interface IAddFileFormDispatchProps {
    onAddFile?: (params: IAddFileAsyncRequestPayload) => void;
}

type IAddFileProps = IAddFileFormProps & IAddFileFormDispatchProps

interface IAddState {
    name: {
        value: string,
        validateStatus: "" | "error" | "success" | "warning" | "validating";
        errorMsg: string;
    };
    type: {
        value: FileType;
    };
}

function validateFileName(name: string): {
    validateStatus: "" | "error" | "success" | "warning" | "validating";
    errorMsg: string;
} {
    if (name === "") {
        return {
            validateStatus: 'error',
            errorMsg: "File name can't be empty",
        };
    }
    if (name.includes('/')) {
        return {
            validateStatus: 'error',
            errorMsg: '"/" is not allow in file name.',
        };
    }
    return {
        validateStatus: 'success',
        errorMsg: '',
    };
}

function hasAddErrors(state: IAddState): boolean {
    if (state.name.validateStatus != 'success') {
        return true
    }
    return false
}

class AddFileForm extends React.Component<IAddFileProps, IAddState> {
    state: IAddState = {
        name: {
            value: '',
            validateStatus: '',
            errorMsg: ''
        },
        type: {
            value: FileType.FILE
        }
    };

    onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        this.setState({
            name: {
                ...this.state.name,
                ...validateFileName(name),
                value: name
            }
        })
    }

    onFileTypeSelect = (value: number) => {
        this.setState({
            type: {
                value: value
            }
        })
    };

    handleSubmit = (e: any): void => {
        if (!hasAddErrors(this.state)) {
            if (this.props.onAddFile) {
                this.props.onAddFile({
                    newFileInfo: {
                        name: this.state.name.value,
                        type: this.state.type.value
                    },
                    parent: this.props.file,
                    isRoot: false
                });

                this.setState({
                    name: {
                        value: '',
                        validateStatus: '',
                        errorMsg: ''
                    }
                })
            }
        }
    };


    render() {
        const formItemLayout = {
            labelCol: {span: 6},
            wrapperCol: {span: 16},
        };
        const {name, type} = this.state;
        const formId = "add_file_form";


        return (
            <Form
                {...formItemLayout}
                id={formId}
                layout="horizontal"
                labelAlign={'left'}
            >
                <Form.Item
                    label={"File Name"}
                    validateStatus={name.validateStatus}
                    help={name.errorMsg}
                >
                    <Input
                        value={name.value}
                        onChange={this.onChangeName}
                        placeholder="Your file/folder name"
                        style={{width: 220}}
                    />
                </Form.Item>
                <Form.Item label={"Variable Type"}>
                    <Select
                        value={type.value}
                        style={{width: 220}}
                        defaultValue={FileType.FILE}
                        onSelect={this.onFileTypeSelect}
                    >
                        <Option value={FileType.FILE}>File</Option>
                        <Option value={FileType.FOLDER}>Folder</Option>
                    </Select>
                </Form.Item>
                <Form.Item>
                    <Button
                        onClick={this.handleSubmit}
                        type="primary"
                        disabled={hasAddErrors(this.state)}
                    >
                        Add
                    </Button>
                </Form.Item>
            </Form>
        )
    }
}


export {AddFileForm};


interface IRenamingFileFormProps {
    file: IFile;
}

export interface IRenamingFileFormDispatchProps {
    onRenameFile?: (file: IFile, newName: string) => void;
    onMoveFile?: (file: IFile, newParentPath: string) => void;
}

type IRenamingFileProps = IRenamingFileFormProps & IRenamingFileFormDispatchProps

interface IRenamingState {
    name: {
        value: string,
        validateStatus: "" | "error" | "success" | "warning" | "validating";
        errorMsg: string;
    };
    parentPath: {
        value?: string,
        validateStatus: "" | "error" | "success" | "warning" | "validating";
        errorMsg: string;
    };
}

function hasRenamingErrors(state: IRenamingState, file:IFile): boolean {
    if (state.name.validateStatus != 'success' || state.name.value === file.name) {
        return true
    }
    return false
}

function hasMovingErrors(state: IRenamingState, file: IFile): boolean {
    if (state.parentPath.validateStatus != 'success' || state.parentPath.value === file.parentPath) {
        return true
    }
    return false
}

function validateParentPath(parentPath: string): {
    validateStatus: "" | "error" | "success" | "warning" | "validating";
    errorMsg: string;
} {
    if (!parentPath || parentPath === "") {
        return {
            validateStatus: 'error',
            errorMsg: "Parent path can't be empty",
        };
    }
    return {
        validateStatus: 'success',
        errorMsg: '',
    };
}

class RenamingFileForm extends React.Component<IRenamingFileProps, IRenamingState> {
    state: IRenamingState

    constructor(props: IRenamingFileProps) {
        super(props)

        this.state = {
            name: {
                value: this.props.file.name,
                validateStatus: '',
                errorMsg: ''
            },
            parentPath: {
                value: this.props.file.parentPath,
                validateStatus: '',
                errorMsg: ''
            }
        };
    }


    onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        this.setState({
            name: {
                ...this.state.name,
                ...validateFileName(name),
                value: name
            }
        })
    }

    onMoveFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const parentPath = e.target.value;
        this.setState({
            parentPath: {
                ...this.state.parentPath,
                ...validateParentPath(parentPath),
                value: parentPath
            }
        })
    }

    handleSubmit = (e: any): void => {
        if (!hasRenamingErrors(this.state, this.props.file)) {
            if (this.props.onRenameFile) {
                this.props.onRenameFile(this.props.file, this.state.name.value);
                this.setState({
                    name: {
                        value: '',
                        validateStatus: '',
                        errorMsg: ''
                    }
                });
            }
        }
    };

    handleMove = (e: any): void => {
        if (!hasMovingErrors(this.state, this.props.file)) {
            if (this.props.onMoveFile && this.state.parentPath.value) {
                this.props.onMoveFile(this.props.file, this.state.parentPath.value);
                this.setState({
                    parentPath: {
                        value: '',
                        validateStatus: '',
                        errorMsg: ''
                    }
                });
            }
        }
    };


    render() {
        const formItemLayout = {
            labelCol: {span: 6},
            wrapperCol: {span: 16},
        };
        const {name, parentPath} = this.state;

        const {file} = this.props

        return (
            <Form
                {...formItemLayout}
                layout="horizontal"
                labelAlign={'left'}
            >
                <SuperAdminZone>
                <div>
                    <div>
                        {file.parentId}: {file.parentPath}
                    </div>
                    <div>
                        {JSON.stringify(file.children)}
                    </div>
                    <div>
                        {file.id}: {file.path}
                    </div>
                    <div>
                        {file.name}
                    </div>
                    <Form.Item
                        label={"Parent Path"}
                        validateStatus={parentPath.validateStatus}
                        help={parentPath.errorMsg}
                    >
                        <Input
                            value={parentPath.value}
                            onChange={this.onMoveFile}
                            placeholder="The parent path you want to move to"
                            style={{width: 220}}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Popconfirm
                            title={`Are you sure to move from "${file.parentPath}" to "${parentPath.value}" ?`}
                            onConfirm={this.handleMove}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                disabled={hasMovingErrors(this.state, this.props.file)}
                            >
                                Move
                            </Button>
                        </Popconfirm>
                    </Form.Item>
                </div>
                </SuperAdminZone>
                <Form.Item
                    label={"File Name"}
                    validateStatus={name.validateStatus}
                    help={name.errorMsg}
                >
                    <Input
                        value={name.value}
                        onChange={this.onChangeName}
                        placeholder="Your file/folder name"
                        style={{width: 220}}
                    />
                </Form.Item>
                <Form.Item>
                    <Popconfirm
                        title={`Are you sure to rename "${file.name}" to "${name.value}" ?`}
                        onConfirm={this.handleSubmit}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="primary"
                            disabled={hasRenamingErrors(this.state, this.props.file)}
                        >
                            Rename
                        </Button>
                    </Popconfirm>
                </Form.Item>
            </Form>
        )
    }
}


export {RenamingFileForm};

